/**
 * CMMS Theme - Unified Styling System
 *
 * This unified theme system centralizes ALL CMMS-specific styling values
 * found across components to eliminate code duplication and ensure design consistency.
 */

export const cmmsTheme = {
	colors: {
		commentsBackground: "#E9F2F8",
		commentsText: "#0000008A",
		surfaceWhite: "#FFF",
		surfaceWhiteImportant: "#FFF !important",
		warningBackground: "#dc3545",
		excelGreen: "#217346",
	},

	spacing: {
		modalSection: "15px",
		inputOffset: "-2px",
		buttonOffset: "2px",
	},

	typography: {
		dialogTitle: {
			fontSize: "1.7em",
			fontWeight: "bold" as const,
		},
		sectionTitle: {
			fontSize: 15,
			fontWeight: "bold" as const,
		},
		semiBold: {
			fontWeight: 600,
		},
	},

	dimensions: {
		standardInput: "40px",
		textAreaMin: "80px",
	},

	styles: {
		commentsCard: {
			backgroundColor: "#E9F2F8",
		},

		commentsTitle: {
			fontSize: 15,
			fontWeight: "bold" as const,
			color: "#0000008A",
		},

		dialogTitle: {
			fontSize: "1.7em",
			fontWeight: "bold" as const,
		},

		filterSelect: {
			backgroundColor: "#FFF",
			height: "40px",
			marginTop: "-2px",
		},

		modalSection: {
			marginTop: "15px",
		},

		commentsTextArea: {
			height: "80px",
			border: "none",
			backgroundColor: "#E9F2F8",
			fontSize: 16,
		},

		whiteBackgroundImportant: {
			"& .MuiOutlinedInput-root": {
				backgroundColor: "#FFF !important",
				"&:hover": {
					backgroundColor: "#FFF !important",
				},
				"&.Mui-focused": {
					backgroundColor: "#FFF !important",
				},
			},
			"& .MuiSelect-select": {
				backgroundColor: "#FFF !important",
				"&:focus": {
					backgroundColor: "#FFF !important",
				},
			},
		},

		logbookTitleInput: {
			fontSize: "1.5em",
			fontWeight: "bold" as const,
			"& .MuiInputBase-input": {
				padding: "0.5rem",
			},
		},

		logbookDatePicker: {
			width: "100%",
			"& .MuiInputBase-root": {
				height: "40px",
				paddingTop: 5,
			},
			"& .MuiOutlinedInput-root": {
				height: "40px",
			},
		},

		warningModal: {
			color: "white",
			backgroundColor: "#dc3545",
			textAlign: "center" as const,
		},

		// Icon-only variant of the Excel / PDF buttons: a filled square the size of the "+" add
		// icon, no label, for toolbars that sit on top of a table and have no room for two full
		// buttons. The label travels in the tooltip instead.
		excelIconButton: {
			width: 56,
			height: 30,
			padding: 0,
			borderRadius: "6px",
			color: "#FFF",
			// A step lighter than the brand green; the brand color itself is the hover.
			backgroundColor: "#298F5C",
			"&:hover": { backgroundColor: "#217346" },
			"&.Mui-disabled": { color: "#FFF", backgroundColor: "#C4CDD5" },
		},

		pdfIconButton: {
			width: 56,
			height: 30,
			padding: 0,
			borderRadius: "6px",
			color: "#FFF",
			// A step lighter than the brand red; the brand color itself is the hover.
			backgroundColor: "#DA3E58",
			"&:hover": { backgroundColor: "#CC2643" },
			"&.Mui-disabled": { color: "#FFF", backgroundColor: "#C4CDD5" },
		},

		// White button with the brand color as text and border, as it always was. The only filled
		// element is the icon chip (`.btn-icon`), which carries the color block. Icon and label are
		// centered together (gap), not pushed to opposite edges.
		excelButton: {
			width: "auto",
			height: 35,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: "8px",
			padding: "0 14px",
			fontSize: 14,
			color: "#217346",
			border: "1px solid #217346",
			backgroundColor: "#FFF",
			"& .btn-icon": { backgroundColor: "#217346", color: "#FFF" },
			"&:hover": { backgroundColor: "#F1F8F4", borderColor: "#217346" },
			"&.Mui-disabled": {
				color: "#9E9E9E",
				backgroundColor: "#FFF",
				borderColor: "#E0E0E0",
			},
			"&.Mui-disabled .btn-icon": { backgroundColor: "#BDBDBD" },
		},

		// Sibling of excelButton for anything that opens or downloads a PDF. Same measurements,
		// the red already used by the PDF download buttons on the drawings tables.
		pdfButton: {
			width: "auto",
			height: 35,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: "8px",
			padding: "0 14px",
			fontSize: 14,
			color: "#CC2643",
			border: "1px solid #CC2643",
			backgroundColor: "#FFF",
			"& .btn-icon": { backgroundColor: "#CC2643", color: "#FFF" },
			"&:hover": { backgroundColor: "#FDF2F4", borderColor: "#CC2643" },
			"&.Mui-disabled": {
				color: "#9E9E9E",
				backgroundColor: "#FFF",
				borderColor: "#E0E0E0",
			},
			"&.Mui-disabled .btn-icon": { backgroundColor: "#BDBDBD" },
		},
	},
} as const;

export const useCmmsTheme = () => cmmsTheme;
